import _ from "lodash";
import React, { useState, useEffect } from "react";
import {
  Modal,
  DSProvider,
  IconButton,
  Tooltip,
  Button,
  Checkbox,
} from "@toddle-edu/ds-web";
import EventTitle from "../EventTitle";
import EventType from "../EventType";
import DateTime from "../DateTime";
import RepeatEvent from "../RepeatEvent";
import EventLocation from "../Location";
import EventParticipants from "../ParticipantsV2";
import Attachments from "../Attachments";
import classes from "./EventCreationModalV2.scss";
import useEventModal from "../../hooks/useEventModal";
import useScrollToBottom from "../../hooks/useScrollToBottom";
import classNames from "classnames";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import {
  mapStateToProps,
  mapActionCreators,
  Z_INDEX,
  EVENT_MODE_LABELS,
  setValue,
} from "../../modules/Utils";
import { connect } from "react-redux";
import { resetEventModalV2 } from "MyCalendar/modules/MyCalendarModule";
import FullScreenEventModal from "../FullScreenEventModalV2";
import ConformationModal from "../ConfirmationModal";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { getSelectedAcademicYearStartDate } from "modules/CommonSelectors";
import { useOperationalDays } from "../../hooks";
import { getSelectedAcademicYearForSchool } from "../../../../modules/Utils";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";

const EventCreationModalV2 = props => {
  const {
    eventModalV2,
    createEventContainerClass,
    updateEventModalV2,
    testId = "",
  } = props;
  const [shouldNotifyParticipants, setShouldNotifyParticipants] = useState(
    eventModalV2?.shouldNotifyParticipants
  );

  useEffect(() => {
    updateEventModalV2({
      calendarEventItemType: MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT,
    });
  }, []);
  const {
    t,
    isModalOpen,
    setIsModalOpen,
    isFullScreenModal,
    closeModal,
    openFullScreenModal,
    showModal,
    isConfirmationModalOpen,
    onDiscardingChanges,
    onCancelDiscardingChanges,
    mode,
    actionButtons,
    showFullScreenLoader,
    isEditMode,
    isRepeatEvent,
  } = useEventModal(props);

  const operationalDays = useOperationalDays();

  const [scrollRef, scrollToBottom] = useScrollToBottom();
  const toggleNotifyParticipants = setValue(
    "shouldNotifyParticipants",
    updateEventModalV2
  );
  const onChangeHandler = () => {
    setShouldNotifyParticipants(!shouldNotifyParticipants);
    toggleNotifyParticipants(!shouldNotifyParticipants);
  };

  const renderModalContent = () => {
    if (isFullScreenModal) {
      return (
        <FullScreenEventModal
          {...props}
          t={t}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isFullScreenModal={isFullScreenModal}
          closeModal={closeModal}
          openFullScreenModal={openFullScreenModal}
          showModal={showModal}
          isConfirmationModalOpen={isConfirmationModalOpen}
          onDiscardingChanges={onDiscardingChanges}
          onCancelDiscardingChanges={onCancelDiscardingChanges}
          mode={mode}
          actionButtons={actionButtons}
          showFullScreenLoader={showFullScreenLoader}
          isEditMode={isEditMode}
          isRepeatEvent={isRepeatEvent}
          operationalDays={operationalDays}
          testId={testId}
        />
      );
    }

    if (showModal) {
      return (
        <Modal
          width="660px"
          height="max-content"
          setIsModalOpen={setIsModalOpen}
          isOpen={isModalOpen}
          onClose={closeModal}
          zIndex={Z_INDEX}
        >
          <Modal.Header isClosable={false} isSticky={true}>
            <div className={classes.container}>
              <div className={classes.left}>
                <div
                  className={classNames("text-label-s", classes.headerHeading)}
                >
                  {t(_.get(EVENT_MODE_LABELS, mode, "calendar:new_event"))}
                </div>
                <EventTitle testId={`${testId}`} />
              </div>
              <div className={classes.right}>
                <Tooltip
                  dsVersion="2.0"
                  tooltip={t("common:close")}
                  showArrow={true}
                  placement="bottom"
                >
                  <IconButton
                    variant="neutral"
                    type="plain"
                    onClick={closeModal}
                    icon={<CloseOutlined variant="subtle" />}
                    size="medium"
                  />
                </Tooltip>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body isScrollable={true}>
            <div className={classes.modalBody}>
              <EventType
                dropdownContainerClass={classes.dropdownContainerClass}
              />
              {/* when click on create button we are not showing selected slot  */}
              <DateTime isSelectedSlotVisible={false} />
              {isRepeatEvent ? (
                <RepeatEvent
                  dropdownContainerClass={classes.dropdownContainerClass}
                  skipNonOperationalDaysClassName={
                    classes.skipNonOperationalDays
                  }
                  operationalDays={operationalDays}
                />
              ) : null}
              <EventParticipants testId={`${testId}`} />
              <EventLocation testId={`${testId}`} />
              <Attachments testId={`${testId}`} />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.footer}>
              <div className={classes.notifyContainer}>
                <Checkbox
                  isChecked={shouldNotifyParticipants}
                  dsVersion="2.0"
                  size="medium"
                  onClick={onChangeHandler}
                  testId={`${testId}-notify-participants`}
                />
                <div className="text-body">
                  {t("calendar:notify_participants_on_creation_of_the_event")}
                </div>
              </div>
              <div className={classes.footerActionContainer}>
                <Button
                  onClick={openFullScreenModal}
                  variant="neutral"
                  type="outlined"
                  testId={`${testId}-more-options`}
                >
                  {t("common:more_options")}
                </Button>
                {_.get(actionButtons, mode, null)}
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      );
    }

    return (
      <div
        className={classNames(
          classes.customModalContainer,
          createEventContainerClass
        )}
      >
        <div className={classes.customModalHeader}>
          <div className={classes.container}>
            <div className={classes.left}>
              <div
                className={classNames("text-label-s", classes.headerHeading)}
              >
                {t(_.get(EVENT_MODE_LABELS, mode, "calendar:new_event"))}
              </div>
              <EventTitle testId={`${testId}`} />
            </div>
            <div className={classes.right}>
              <Tooltip
                dsVersion="2.0"
                tooltip={t("common:close")}
                showArrow={true}
                placement="bottom"
              >
                <IconButton
                  variant="neutral"
                  type="plain"
                  onClick={closeModal}
                  icon={<CloseOutlined variant="subtle" />}
                  size="medium"
                />
              </Tooltip>
            </div>
          </div>
        </div>
        <div ref={scrollRef} className={classes.customModalBody}>
          <EventType dropdownContainerClass={classes.dropdownContainerClass} />
          <DateTime isSelectedSlotVisible={true} />
          {isRepeatEvent ? (
            <RepeatEvent
              dropdownContainerClass={classes.dropdownContainerClass}
              skipNonOperationalDaysClassName={classes.skipNonOperationalDays}
              operationalDays={operationalDays}
            />
          ) : null}
          <EventParticipants testId={`${testId}`} />
          <EventLocation testId={`${testId}`} />
          <Attachments testId={`${testId}`} />
        </div>
        <div className={classes.customFooter}>
          <div className={classes.notifyContainer}>
            <Checkbox
              isChecked={shouldNotifyParticipants}
              dsVersion="2.0"
              size="medium"
              onClick={onChangeHandler}
              testId={`${testId}-notify-participants`}
            />
            <div className="text-body">
              {t("calendar:notify_participants_on_creation_of_the_event")}
            </div>
          </div>
          <div className={classes.customModalfooter}>
            <Button
              onClick={openFullScreenModal}
              variant="neutral"
              type="outlined"
              testId={`${testId}-more-options`}
            >
              {t("common:more_options")}
            </Button>
            {_.get(actionButtons, mode, null)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DSProvider dsVersion="2.0">
      {renderModalContent()}
      <ConformationModal
        isConfirmationModalOpen={isConfirmationModalOpen}
        t={t}
        onCancelDiscardingChanges={onCancelDiscardingChanges}
        onDiscardingChanges={onDiscardingChanges}
      />
      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </DSProvider>
  );
};

export const localMapActionCreators = {
  ...mapActionCreators,
  resetEventModalV2,
};

const localMapStateToProps = state => {
  const selectedAcademicYear = getSelectedAcademicYearForSchool(state);
  const selectedAcademicYearStartDate = getSelectedAcademicYearStartDate({
    selectedAcademicYear,
  });
  return {
    ...mapStateToProps(state),
    selectedAcademicYearStartDate,
  };
};

export default connect(
  localMapStateToProps,
  localMapActionCreators
)(EventCreationModalV2);
